<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { OnClickOutside } from '@vueuse/components';
import { useStore } from 'dashboard/composables/store';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import SettingsLayout from 'dashboard/routes/dashboard/settings/SettingsLayout.vue';
import { fetchWooCommerceOrderDetails } from '../../api/woocommerce';
import {
  buildConversationRoute,
  buildWooCommerceContactRoute,
  buildWooCommerceOrdersRoute,
  getLatestConversation,
} from '../../helpers/navigation';

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t, locale } = useI18n();

const isLoading = ref(true);
const error = ref('');
const order = ref(null);
const isActionMenuOpen = ref(false);

const accountId = computed(() => Number(route.params.accountId));
const normalizedIntlLocale = computed(() =>
  locale.value?.toString().replace(/_/g, '-') || undefined
);
const orderRecordId = computed(
  () => route.params.orderRecordId?.toString() || ''
);
const backRoute = computed(() =>
  buildWooCommerceOrdersRoute(
    accountId.value,
    route.query.hook_id?.toString() || '',
    {
      page: route.query.page?.toString() || undefined,
      search: route.query.search?.toString() || undefined,
      store_name: route.query.store_name?.toString() || undefined,
    }
  )
);
const translatedError = computed(() =>
  error.value ? t('WOOCOMMERCE_PAGES.ORDER_DETAILS.ERROR') : ''
);

function formatCurrency(amount, currency) {
  const numericAmount = Number(amount);
  if (Number.isNaN(numericAmount)) {
    return [amount || '—', currency || ''].filter(Boolean).join(' ');
  }

  try {
    return new Intl.NumberFormat(normalizedIntlLocale.value, {
      style: 'currency',
      currency: currency || 'USD',
    }).format(numericAmount);
  } catch {
    return [amount || '—', currency || ''].filter(Boolean).join(' ');
  }
}

function formatDateTime(value) {
  const parsedValue = Date.parse(value || '');
  if (Number.isNaN(parsedValue)) {
    return '—';
  }

  const date = new Date(parsedValue);

  try {
    return new Intl.DateTimeFormat(normalizedIntlLocale.value, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(date);
  } catch {
    return date.toLocaleString();
  }
}

function formatAddress(address) {
  if (!address) return '—';

  const lines = [
    [address.first_name, address.last_name].filter(Boolean).join(' ').trim(),
    address.company,
    address.address_1,
    address.address_2,
    [address.city, address.state].filter(Boolean).join(', ').trim(),
    [address.postcode, address.country].filter(Boolean).join(' ').trim(),
  ].filter(Boolean);

  return lines.length ? lines.join(' · ') : '—';
}

function getLineItemImageUrl(item) {
  return (
    item?.image?.src ||
    item?.image?.url ||
    item?.images?.[0]?.src ||
    item?.images?.[0]?.url ||
    item?.product?.image?.src ||
    item?.product?.image?.url ||
    item?.product_image ||
    item?.product_image_url ||
    item?.image_url ||
    item?.thumbnail_url ||
    item?.thumbnail ||
    ''
  );
}

function getLineItemProductUrl(item) {
  return (
    item?.permalink ||
    item?.product_url ||
    item?.product_permalink ||
    item?.url ||
    item?.product?.permalink ||
    item?.product?.url ||
    item?.product?.external_url ||
    ''
  );
}

function openProductPage(item) {
  const productUrl = getLineItemProductUrl(item);
  if (!productUrl) return;

  window.open(productUrl, '_blank', 'noopener,noreferrer');
}

const summaryRows = computed(() => {
  if (!order.value) return [];

  return [
    {
      label: t('WOOCOMMERCE_PAGES.ORDER_DETAILS.SUMMARY.STATUS'),
      value: order.value.status || '—',
    },
    {
      label: t('WOOCOMMERCE_PAGES.ORDER_DETAILS.SUMMARY.PAYMENT_STATUS'),
      value: order.value.payment_status || '—',
    },
    {
      label: t('WOOCOMMERCE_PAGES.ORDER_DETAILS.SUMMARY.TOTAL'),
      value: formatCurrency(order.value.total, order.value.currency),
    },
    {
      label: t('WOOCOMMERCE_PAGES.ORDER_DETAILS.SUMMARY.STORE'),
      value: order.value.source_label || order.value.store_url || '—',
    },
    {
      label: t('WOOCOMMERCE_PAGES.ORDER_DETAILS.SUMMARY.CREATED_AT'),
      value: formatDateTime(order.value.created_at),
    },
    {
      label: t('WOOCOMMERCE_PAGES.ORDER_DETAILS.SUMMARY.UPDATED_AT'),
      value: formatDateTime(order.value.updated_at),
    },
  ];
});
const customerRows = computed(() => {
  if (!order.value) return [];

  return [
    {
      label: t('WOOCOMMERCE_PAGES.ORDER_DETAILS.CUSTOMER.NAME'),
      value: order.value.customer_name || '—',
    },
    {
      label: t('WOOCOMMERCE_PAGES.ORDER_DETAILS.CUSTOMER.EMAIL'),
      value: order.value.customer_email || '—',
    },
    {
      label: t('WOOCOMMERCE_PAGES.ORDER_DETAILS.CUSTOMER.PHONE'),
      value: order.value.customer_phone || '—',
    },
  ];
});
const billingAddress = computed(() => formatAddress(order.value?.billing));
const shippingAddress = computed(() => formatAddress(order.value?.shipping));

const loadOrder = async () => {
  try {
    isActionMenuOpen.value = false;
    isLoading.value = true;
    error.value = '';

    const { data } = await fetchWooCommerceOrderDetails(
      accountId.value,
      orderRecordId.value,
      {
        hook_id: route.query.hook_id?.toString() || undefined,
      }
    );

    if (!data?.order) {
      throw new Error('WooCommerce order not found');
    }

    order.value = data.order;
  } catch {
    error.value = 'WOOCOMMERCE_PAGES.ORDER_DETAILS.ERROR';
    order.value = null;
  } finally {
    isLoading.value = false;
  }
};

const openExternalOrder = () => {
  isActionMenuOpen.value = false;
  if (!order.value?.admin_url) return;

  window.open(order.value.admin_url, '_blank', 'noopener,noreferrer');
};

const openContact = () => {
  isActionMenuOpen.value = false;
  if (!order.value?.contact_id) return;

  router.push(
    buildWooCommerceContactRoute(accountId.value, order.value.contact_id)
  );
};

const openLatestConversation = async () => {
  isActionMenuOpen.value = false;
  if (!order.value?.contact_id) return;

  await store.dispatch('contactConversations/get', order.value.contact_id);
  const conversationsGetter =
    store.getters['contactConversations/getContactConversation'];
  const conversations = conversationsGetter
    ? conversationsGetter(order.value.contact_id)
    : [];
  const latestConversation = getLatestConversation(conversations);

  if (!latestConversation?.id) {
    openContact();
    return;
  }

  router.push(buildConversationRoute(accountId.value, latestConversation.id));
};

onMounted(() => {
  loadOrder();
});

watch(
  () => route.params.orderRecordId,
  () => {
    loadOrder();
  }
);
</script>

<template>
  <SettingsLayout :is-loading="isLoading" class="gap-6">
    <template #header>
      <div
        data-target="woocommerce-order-details-page"
        class="w-full max-w-[72rem] mx-auto px-4 sm:px-6 pt-4 sm:pt-6"
      >
        <div class="rounded-md border border-n-weak bg-n-alpha-2 p-4 sm:p-5">
          <div class="flex flex-col gap-4">
            <div class="flex min-w-0 flex-col gap-3">
              <Button
                icon="i-lucide-arrow-left"
                variant="ghost"
                color="slate"
                size="sm"
                class="w-full justify-center sm:w-fit"
                :label="$t('WOOCOMMERCE_PAGES.ORDER_DETAILS.BACK_TO_ORDERS')"
                @click="router.push(backRoute)"
              />
              <div>
                <h1 class="mb-0 text-2xl font-semibold text-n-slate-12">
                  {{
                    $t('WOOCOMMERCE_PAGES.ORDERS.ORDER_NUMBER', {
                      number: order?.number || order?.id || '—',
                    })
                  }}
                </h1>
                <p class="mb-0 text-sm text-n-slate-11">
                  {{ $t('WOOCOMMERCE_PAGES.ORDER_DETAILS.DESCRIPTION') }}
                </p>
              </div>
            </div>

            <div class="relative flex w-full items-center gap-2 sm:w-auto">
              <Button
                v-if="order?.admin_url"
                icon="i-lucide-external-link"
                size="sm"
                class="min-w-0 flex-1 justify-center text-sm sm:flex-none"
                :label="$t('WOOCOMMERCE_PAGES.OPEN_IN_WOOCOMMERCE')"
                @click="openExternalOrder"
              />
              <OnClickOutside @trigger="isActionMenuOpen = false">
                <div class="relative">
                  <button
                    type="button"
                    class="flex size-9 items-center justify-center rounded-md border border-n-weak bg-n-alpha-2 text-n-slate-11 transition hover:bg-n-alpha-3 hover:text-n-slate-12"
                    :aria-label="$t('WOOCOMMERCE_PAGES.MORE_ACTIONS')"
                    :title="$t('WOOCOMMERCE_PAGES.MORE_ACTIONS')"
                    @click="isActionMenuOpen = !isActionMenuOpen"
                  >
                    <span class="i-lucide-ellipsis-vertical size-4" />
                  </button>
                  <div
                    v-if="isActionMenuOpen"
                    class="absolute right-0 top-11 z-20 w-64 overflow-hidden rounded-md border border-n-weak bg-n-solid-2 p-1 shadow-lg"
                  >
                    <button
                      type="button"
                      class="flex w-full items-center gap-2 rounded px-3 py-2 text-left text-sm text-n-slate-12 hover:bg-n-alpha-2 disabled:cursor-not-allowed disabled:opacity-50"
                      :disabled="!order?.contact_id"
                      @click="openContact"
                    >
                      <span class="i-lucide-user-round size-4 text-n-slate-10" />
                      <span>{{ $t('WOOCOMMERCE_PAGES.OPEN_CONTACT_TO_MESSAGE') }}</span>
                    </button>
                    <button
                      type="button"
                      class="flex w-full items-center gap-2 rounded px-3 py-2 text-left text-sm text-n-slate-12 hover:bg-n-alpha-2 disabled:cursor-not-allowed disabled:opacity-50"
                      :disabled="!order?.contact_id"
                      @click="openLatestConversation"
                    >
                      <span class="i-lucide-message-circle size-4 text-n-slate-10" />
                      <span>{{ $t('WOOCOMMERCE_PAGES.OPEN_LAST_CONVERSATION') }}</span>
                    </button>
                  </div>
                </div>
              </OnClickOutside>
            </div>
          </div>
        </div>
      </div>
    </template>

    <template #body>
      <div class="w-full max-w-[72rem] mx-auto px-4 sm:px-6 pb-6 flex flex-col gap-4 sm:gap-6">
        <div
          v-if="error"
          class="rounded-md border border-n-ruby-5 bg-n-ruby-2 p-4 text-sm text-n-ruby-12"
        >
          {{ translatedError }}
        </div>

        <template v-else-if="order">
          <div
            class="grid gap-4 sm:gap-6 xl:grid-cols-[minmax(0,1.7fr)_minmax(20rem,1fr)]"
          >
            <div class="grid gap-4 sm:gap-6">
              <section class="rounded-md border border-n-weak bg-n-alpha-2 p-4 sm:p-5">
                <h2 class="mb-4 text-base font-semibold text-n-slate-12">
                  {{ $t('WOOCOMMERCE_PAGES.ORDER_DETAILS.ITEMS.TITLE') }}
                </h2>

                <div v-if="order.line_items?.length" class="grid gap-3">
                  <button
                    v-for="item in order.line_items"
                    :key="item.id || item.product_id || item.name"
                    type="button"
                    class="w-full rounded-md border border-n-weak bg-n-alpha-3 p-3 text-left transition hover:border-n-blue-7 hover:bg-n-alpha-2 disabled:cursor-default disabled:opacity-100 disabled:hover:border-n-weak disabled:hover:bg-n-alpha-3 sm:p-4"
                    :disabled="!getLineItemProductUrl(item)"
                    :title="getLineItemProductUrl(item) || undefined"
                    @click="openProductPage(item)"
                  >
                    <div
                      class="flex items-center gap-3 sm:items-start sm:justify-between"
                    >
                      <div class="flex min-w-0 flex-1 items-center gap-3">
                        <div
                          class="flex size-14 shrink-0 items-center justify-center overflow-hidden rounded-md border border-n-weak bg-n-alpha-2"
                        >
                          <img
                            v-if="getLineItemImageUrl(item)"
                            :src="getLineItemImageUrl(item)"
                            :alt="
                              item.name ||
                              $t('WOOCOMMERCE_PAGES.ORDER_DETAILS.ITEMS.UNNAMED')
                            "
                            class="size-full object-cover"
                            loading="lazy"
                          />
                          <span
                            v-else
                            class="i-lucide-package size-5 text-n-slate-10"
                          />
                        </div>
                        <div class="min-w-0">
                          <p class="mb-1 text-sm font-semibold text-n-slate-12">
                            {{
                              item.name ||
                              $t('WOOCOMMERCE_PAGES.ORDER_DETAILS.ITEMS.UNNAMED')
                            }}
                          </p>
                          <p class="mb-0 text-xs text-n-slate-10">
                            {{
                              $t(
                                'WOOCOMMERCE_PAGES.ORDER_DETAILS.ITEMS.QUANTITY',
                                {
                                  quantity: item.quantity || 0,
                                }
                              )
                            }}
                          </p>
                        </div>
                      </div>
                      <div class="flex shrink-0 items-center gap-2">
                        <p class="mb-0 text-sm font-semibold text-n-slate-12 sm:text-right">
                          {{ formatCurrency(item.total, order.currency) }}
                        </p>
                        <span
                          v-if="getLineItemProductUrl(item)"
                          class="i-lucide-external-link size-4 text-n-slate-10"
                        />
                      </div>
                    </div>
                  </button>
                </div>
                <p v-else class="mb-0 text-sm text-n-slate-11">
                  {{ $t('WOOCOMMERCE_PAGES.ORDER_DETAILS.ITEMS.EMPTY') }}
                </p>
              </section>

              <section class="grid gap-4 sm:gap-6 lg:grid-cols-2">
                <div class="rounded-md border border-n-weak bg-n-alpha-2 p-4 sm:p-5">
                  <h2 class="mb-3 text-base font-semibold text-n-slate-12">
                    {{
                      $t('WOOCOMMERCE_PAGES.ORDER_DETAILS.ADDRESSES.BILLING')
                    }}
                  </h2>
                  <p class="mb-0 text-sm text-n-slate-11">
                    {{ billingAddress }}
                  </p>
                </div>

                <div class="rounded-md border border-n-weak bg-n-alpha-2 p-4 sm:p-5">
                  <h2 class="mb-3 text-base font-semibold text-n-slate-12">
                    {{
                      $t('WOOCOMMERCE_PAGES.ORDER_DETAILS.ADDRESSES.SHIPPING')
                    }}
                  </h2>
                  <p class="mb-0 text-sm text-n-slate-11">
                    {{ shippingAddress }}
                  </p>
                </div>
              </section>

              <section
                v-if="order.customer_note"
                class="rounded-md border border-n-weak bg-n-alpha-2 p-4 sm:p-5"
              >
                <h2 class="mb-3 text-base font-semibold text-n-slate-12">
                  {{ $t('WOOCOMMERCE_PAGES.ORDER_DETAILS.NOTE_TITLE') }}
                </h2>
                <p class="mb-0 text-sm text-n-slate-11 whitespace-pre-wrap">
                  {{ order.customer_note }}
                </p>
              </section>
            </div>

            <aside class="grid gap-4 sm:gap-6 h-fit">
              <section class="rounded-md border border-n-weak bg-n-alpha-2 p-4 sm:p-5">
                <h2 class="mb-4 text-base font-semibold text-n-slate-12">
                  {{ $t('WOOCOMMERCE_PAGES.ORDER_DETAILS.SUMMARY.TITLE') }}
                </h2>
                <div class="grid gap-3">
                  <div
                    v-for="row in summaryRows"
                    :key="row.label"
                    class="flex items-start justify-between gap-4 border-b border-n-weak pb-2 last:border-b-0 last:pb-0"
                  >
                    <span class="text-sm text-n-slate-10">{{ row.label }}</span>
                    <span class="min-w-0 text-sm font-medium text-n-slate-12 text-right">{{
                      row.value
                    }}</span>
                  </div>
                </div>
              </section>

              <section class="rounded-md border border-n-weak bg-n-alpha-2 p-4 sm:p-5">
                <h2 class="mb-4 text-base font-semibold text-n-slate-12">
                  {{ $t('WOOCOMMERCE_PAGES.ORDER_DETAILS.CUSTOMER.TITLE') }}
                </h2>
                <div class="grid gap-3">
                  <div
                    v-for="row in customerRows"
                    :key="row.label"
                    class="flex items-start justify-between gap-4 border-b border-n-weak pb-2 last:border-b-0 last:pb-0"
                  >
                    <span class="text-sm text-n-slate-10">{{ row.label }}</span>
                    <span class="min-w-0 break-words text-sm font-medium text-n-slate-12 text-right">{{
                      row.value
                    }}</span>
                  </div>
                </div>
              </section>
            </aside>
          </div>
        </template>
      </div>
    </template>
  </SettingsLayout>
</template>
